// FassalkartAI — Client environment surface
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS FILE EXISTS — read before adding anything to it.
//
// Create React App inlines env vars with webpack's DefinePlugin, which only
// substitutes *statically written* member access:
//
//     process.env.REACT_APP_FOO        -> replaced with the literal value  ✅
//     process.env[someVariable]        -> forces webpack to inline the ENTIRE
//                                         process.env object into the bundle  ❌
//
// The second form is how every REACT_APP_* value — including the Razorpay live
// secret and the Sentinel Hub client secret — ended up readable in
// build/static/js/*.js even though no source file referenced them by name.
//
// So: this module is the ONLY place allowed to touch process.env, every access
// here is static, and only CLIENT-SAFE values appear. A value is client-safe
// only if it is designed to be public and is protected by a server-side
// restriction (HTTP referrer allow-list, API restriction, Firestore rules).
//
// NEVER add a secret here. Anything secret belongs in Cloud Functions env /
// Secret Manager, reached through a callable. See SECURITY_ROTATION.md.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Client-safe configuration. Every entry is a static process.env access.
 *
 * Firebase web config is public by design — access is controlled by Firestore
 * rules and by API-key restrictions, not by hiding these values.
 */
export const CLIENT_ENV = {
  FIREBASE_API_KEY:             process.env.REACT_APP_FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN:         process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID:          process.env.REACT_APP_FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET:      process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID:              process.env.REACT_APP_FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID:      process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,

  // Publishable identifiers, safe in a browser by design.
  // (Razorpay removed for now — online payments are disabled; COD only.)
  RECAPTCHA_SITE_KEY:           process.env.REACT_APP_RECAPTCHA_SITE_KEY,

  // Restricted at the Google Cloud console by HTTP referrer + API allow-list.
  GOOGLE_MAPS_KEY:              process.env.REACT_APP_GOOGLE_MAPS_KEY,

  // Public Cloud Function endpoints (auth is per-request, not in the URL).
  WEATHER_CF_URL:               process.env.REACT_APP_WEATHER_CF_URL,

  NODE_ENV:                     process.env.NODE_ENV,
};

const PLACEHOLDERS = new Set([
  "your_key_here",
  "paste_here",
  "your_api_key",
  "YOUR_KEY",
  "undefined",
  "null",
  "",
]);

/**
 * @param {string|undefined} value
 * @returns {boolean} true when the value is actually usable
 */
export function isConfigured(value) {
  return typeof value === "string" && !PLACEHOLDERS.has(value.trim());
}

/**
 * Read one client-safe value by its short name.
 * @param {keyof CLIENT_ENV} name
 * @returns {string|undefined}
 */
export function clientEnv(name) {
  return CLIENT_ENV[name];
}

export default CLIENT_ENV;
